import { useGetTessaChatMessages } from "@gettactic/hooks";
import React from "react";
import { FullPageLoader } from "@v2/components/loaders/FullPageLoader/FullPageLoader";
import { TessaResponseMessages } from "@webV2/pages/Tessa/types";
import { TessaChatMessageBubble } from "@webV2/pages/Tessa/TessaChatMessageBubble";
import { TacticLoader } from "@v2/components/loaders/TacticLoader/TacticLoader";
import { ShimmerText } from "@webV2/pages/Tessa/ShimmerText";
import { cn } from "@gettactic/helpers";
import { useAskTessa } from "@v2/components/ai/AskTessaProvider";

export interface ChatBotConversationProps {
  chatId: string;
  liveMessages: TessaResponseMessages[];
  isThinking: boolean;
  isBubble?: boolean;
  className?: string;
  isNewChat?: boolean;
}

// Messages priority when the message timestamp is the same
const messagesSortPriority = {
  user: 1,
  assistant: 2,
  tool: 3
} as const;

export const ChatBotConversation: React.FC<ChatBotConversationProps> = ({
  chatId,
  liveMessages,
  isThinking,
  className,
  isBubble,
  isNewChat
}) => {
  const askTessa = useAskTessa();
  const isNewlyCreated = chatId && askTessa?.newlyCreatedChatIds?.has(chatId);

  // Only fetch history if:
  // 1. We have a valid chat ID
  // 2. It's not a "new" placeholder
  // 3. It's not in "new chat" UI mode
  // 4. It's not currently in the newly created set
  // 5. We don't have live messages (if we have live messages, use those instead)
  // 6. We're not currently thinking (prevents fetching stale history during in-flight exchange)
  const shouldFetchHistory =
    !!chatId &&
    chatId !== "new" &&
    !isNewChat &&
    !isNewlyCreated &&
    liveMessages.length === 0 &&
    !isThinking;

  const { messages, isFetching: isLoading } = useGetTessaChatMessages({
    chatId,
    enabled: shouldFetchHistory
  });

  const { isDocked, isPopup } = askTessa || {
    isDocked: false,
    isPopup: false
  };

  const scrollRef = React.useRef<HTMLDivElement>(null);

  const historyMessages = React.useMemo(() => {
    if (!messages?.elements) {
      return [];
    }

    return (
      messages.elements
        .sort((a, b) => {
          // First: normal chronological sorting
          if (a.created !== b.created) {
            return a.created > b.created ? 1 : -1;
          }

          return messagesSortPriority[a.role] - messagesSortPriority[b.role];
        })
        .map((message) => (
          <TessaChatMessageBubble
            key={message.id}
            message={message}
            reversedParts={false}
          />
        )) || []
    );
  }, [messages]);

  // 🔽 Auto-scroll to bottom on updates
  React.useEffect(() => {
    const el = scrollRef.current;
    if (el) {
      el.scrollTo({
        top: el.scrollHeight,
        behavior: "auto"
      });
    }
  }, [liveMessages, isThinking, messages?.elements?.length]);

  const hasNoMessages =
    historyMessages.length === 0 && liveMessages.length === 0;
  const isInitialLoad = isLoading && hasNoMessages;

  if (hasNoMessages && (isThinking || isInitialLoad)) {
    return (
      <div className="absolute inset-0 mx-auto items-center gap-4 overflow-auto px-20 py-20">
        <div className="mx-auto mt-10 flex w-full max-w-[1200px] items-center gap-4">
          <TacticLoader className="opacity-60" size="xs" />
          <ShimmerText className="text-sm" />
        </div>
      </div>
    );
  }

  return (
    <div
      ref={scrollRef}
      className={cn(
        "absolute inset-0 mx-auto items-center gap-4 overflow-auto px-20 py-20",
        isPopup ? "px-3" : undefined,
        className
      )}
    >
      {historyMessages}
      {liveMessages.map((message) => (
        <TessaChatMessageBubble
          key={message.id}
          message={message}
          reversedParts={false}
        />
      ))}

      {isThinking && (
        <div className="mx-auto mt-10 flex w-full max-w-[1200px] items-center gap-4">
          <TacticLoader className="opacity-60" size="xs" />
          <ShimmerText className="text-sm" />
        </div>
      )}
      {!isBubble && <div className={"h-[250px]"} />}
      <FullPageLoader
        isLoading={isLoading}
        className="pointer-events-none absolute"
      />
    </div>
  );
};
